import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Crosshair, MapPin } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { ASAP_TIME, SERVICE_NAMES, TIME_SLOTS, serviceImage, serviceTint } from '@/lib/domain';
import { describeCoords, getCurrentPosition, type Coords } from '@/lib/geo';
import { useUserId } from '@/components/AuthProvider';
import { PageHeader, Modal } from '@/components/ui';
import MapPicker from '@/components/MapPicker';
import Spinner from '@/components/Spinner';
import styles from './CreateBooking.module.css';

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

type DateChoice = 'asap' | 'today' | 'tomorrow' | 'other';

const DATE_CHOICES: { value: DateChoice; label: string }[] = [
  { value: 'asap', label: ASAP_TIME },
  { value: 'today', label: "Aujourd'hui" },
  { value: 'tomorrow', label: 'Demain' },
  { value: 'other', label: 'Autre date' },
];

const toIsoDate = (value: Date) =>
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(
    value.getDate()
  ).padStart(2, '0')}`;

const dayOffsetIso = (offset: number) => {
  const value = new Date();
  value.setDate(value.getDate() + offset);
  return toIsoDate(value);
};

/** A slot is still bookable today only if its hour is after the current one. */
const isPastSlotToday = (slot: string) => Number.parseInt(slot, 10) <= new Date().getHours();

function CalendarPicker({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (iso: string) => void;
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const firstDay = new Date(viewYear, viewMonth, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday-based
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells: (number | null)[] = [
    ...Array<null>(startOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear((year) => year - 1);
      setViewMonth(11);
    } else {
      setViewMonth((month) => month - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewYear((year) => year + 1);
      setViewMonth(0);
    } else {
      setViewMonth((month) => month + 1);
    }
  };

  return (
    <div className={styles.calendar}>
      <div className={styles.calendarNav}>
        <button type="button" className={styles.calendarNavBtn} onClick={prevMonth} aria-label="Mois précédent">
          <ChevronLeft size={18} />
        </button>
        <span className={styles.calendarTitle}>
          {MONTHS_FR[viewMonth]} {viewYear}
        </span>
        <button type="button" className={styles.calendarNavBtn} onClick={nextMonth} aria-label="Mois suivant">
          <ChevronRight size={18} />
        </button>
      </div>

      <div className={styles.calendarGrid}>
        {DAYS_FR.map((day) => (
          <span key={day} className={styles.calendarDayHead}>
            {day}
          </span>
        ))}

        {cells.map((day, index) => {
          if (!day) return <span key={`empty-${index}`} />;
          const cellDate = new Date(viewYear, viewMonth, day);
          const isPast = cellDate < today;
          const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isSelected = iso === selected;

          return (
            <button
              key={iso}
              type="button"
              className={`${styles.calendarCell} ${isSelected ? styles.calendarCellOn : ''}`}
              onClick={() => onSelect(iso)}
              disabled={isPast}
              aria-pressed={isSelected}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CreateBooking() {
  const navigate = useNavigate();
  const userId = useUserId();
  const [searchParams] = useSearchParams();
  // Set when arriving from a service card or a provider profile.
  const serviceParam = searchParams.get('service');
  const providerParam = searchParams.get('provider');

  const [services, setServices] = useState<{ id: string; name: string }[]>([]);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [providerName, setProviderName] = useState('');
  const [address, setAddress] = useState('');
  const [coords, setCoords] = useState<Coords | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [description, setDescription] = useState('');
  const [dateChoice, setDateChoice] = useState<DateChoice | null>(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    void (async () => {
      const { data } = await supabase.from('services').select('id, name').in('name', SERVICE_NAMES);
      if (data) {
        setServices(
          [...data].sort(
            (a, b) => SERVICE_NAMES.indexOf(a.name) - SERVICE_NAMES.indexOf(b.name)
          )
        );
        const preselected = data.find((service) => service.name === serviceParam);
        if (preselected) setSelectedService(preselected.id);
      }
    })();
  }, [serviceParam]);

  useEffect(() => {
    if (!providerParam) return;
    void (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', providerParam)
        .maybeSingle();
      setProviderName(data?.full_name ?? '');
    })();
  }, [providerParam]);

  const todayIso = dayOffsetIso(0);
  const todaySlotsLeft = TIME_SLOTS.some((slot) => !isPastSlotToday(slot));

  const pickDateChoice = (choice: DateChoice) => {
    setDateChoice(choice);
    if (choice === 'asap') {
      setDate(todayIso);
      setTime(ASAP_TIME);
      return;
    }
    const nextDate = choice === 'today' ? todayIso : choice === 'tomorrow' ? dayOffsetIso(1) : '';
    setDate(nextDate);
    if (time === ASAP_TIME || (nextDate === todayIso && isPastSlotToday(time))) setTime('');
  };

  const pickCalendarDate = (iso: string) => {
    setDate(iso);
    if (iso === todayIso && time && isPastSlotToday(time)) setTime('');
  };

  const applyCoords = async (next: Coords) => {
    setCoords(next);
    setLocating(true);
    try {
      setAddress(await describeCoords(next));
    } finally {
      setLocating(false);
    }
  };

  const useCurrentLocation = async () => {
    setError('');
    setLocating(true);
    try {
      // Applied directly: the preview below lets the client adjust the point if needed.
      await applyCoords(await getCurrentPosition());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Localisation indisponible.');
    } finally {
      setLocating(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (!selectedService || !address.trim() || !date || !time) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    if (!userId) {
      setError('Erreur : utilisateur non trouvé.');
      return;
    }

    setLoading(true);
    try {
      const { error: bookingError } = await supabase.from('bookings').insert({
        client_id: userId,
        service_id: selectedService,
        address: address.trim(),
        description: description.trim(),
        preferred_date: date,
        preferred_time: time,
        status: 'pending',
      });

      if (bookingError) {
        setError(bookingError.message);
        return;
      }

      navigate('/app/bookings', { replace: true });
    } catch {
      setError('Une erreur est survenue. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader title="Nouvelle demande" back="/app" />

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {error ? (
          <p className="alert alert--error" role="alert">
            {error}
          </p>
        ) : null}

        {providerName ? (
          <p className="alert alert--info">
            Demande pour {providerName}. Elle sera aussi visible par les autres prestataires de ce
            service dans votre ville.
          </p>
        ) : null}

        <div className={styles.columns}>
          <div className={styles.column}>
            <section className={styles.section}>
              <h2 className="section-title">
                Sélectionnez un service <span className="req">*</span>
              </h2>
              <div className={styles.serviceGrid}>
                {services.map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    className={`${styles.serviceOption} ${
                      selectedService === service.id ? styles.serviceOptionOn : ''
                    }`}
                    onClick={() => setSelectedService(service.id)}
                    aria-pressed={selectedService === service.id}
                    disabled={loading}
                  >
                    <span className={styles.serviceArt} style={{ background: serviceTint(service.name) }}>
                      <img className={styles.serviceIcon} src={serviceImage(service.name)} alt="" />
                    </span>
                    <span className={styles.serviceName}>{service.name}</span>
                  </button>
                ))}
              </div>
            </section>

            <section className={styles.section}>
              <h2 className="section-title">
                Adresse du service <span className="req">*</span>
              </h2>

              <div className={styles.locationActions}>
                <button
                  type="button"
                  className={styles.locationBtn}
                  onClick={() => void useCurrentLocation()}
                  disabled={loading || locating}
                >
                  <Crosshair size={16} /> Utiliser ma position
                </button>
                <button
                  type="button"
                  className={styles.locationBtn}
                  onClick={() => setMapOpen(true)}
                  disabled={loading || locating}
                >
                  <MapPin size={16} /> Choisir sur la carte
                </button>
              </div>

              {locating ? (
                <p className={styles.locating}>
                  <Spinner size={15} color="var(--ink)" /> Localisation en cours...
                </p>
              ) : null}

              {coords ? (
                <>
                  <p className="hint">
                    Point sélectionné : {coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)}.
                    Touchez la carte pour l&apos;ajuster.
                  </p>
                  <button
                    type="button"
                    className={styles.mapPreview}
                    onClick={() => setMapOpen(true)}
                    aria-label="Ouvrir la carte"
                  >
                    <MapPicker value={coords} height={180} preview />
                  </button>
                </>
              ) : (
                <p className="hint">
                  Saisissez l&apos;adresse, utilisez votre position ou choisissez un point sur la carte.
                </p>
              )}

              <textarea
                className="textarea"
                style={{ minHeight: 72 }}
                placeholder="Ex. 12 rue des Orangers, appartement 4"
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                disabled={loading}
                aria-label="Adresse du service"
              />
            </section>

            <section className={styles.section}>
              <h2 className="section-title">Description du service</h2>
              <textarea
                className="textarea"
                placeholder="Décrivez précisément ce dont vous avez besoin..."
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                disabled={loading}
              />
            </section>
          </div>

          <div className={styles.column}>
            <section className={styles.section}>
              <h2 className="section-title">
                Quand ? <span className="req">*</span>
              </h2>
              <div className={styles.timeGrid}>
                {DATE_CHOICES.map((choice) => (
                  <button
                    key={choice.value}
                    type="button"
                    className={dateChoice === choice.value ? 'chip chip--on' : 'chip'}
                    onClick={() => pickDateChoice(choice.value)}
                    aria-pressed={dateChoice === choice.value}
                    disabled={loading || (choice.value === 'today' && !todaySlotsLeft)}
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
              {dateChoice === 'other' ? (
                <CalendarPicker selected={date} onSelect={pickCalendarDate} />
              ) : null}
            </section>

            {dateChoice && dateChoice !== 'asap' ? (
              <section className={styles.section}>
                <h2 className="section-title">
                  Heure <span className="req">*</span>
                </h2>
                <div className={styles.timeGrid}>
                  {TIME_SLOTS.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      className={time === slot ? 'chip chip--on' : 'chip'}
                      onClick={() => setTime(slot)}
                      aria-pressed={time === slot}
                      disabled={loading || (date === todayIso && isPastSlotToday(slot))}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            <button type="submit" className="btn btn--primary btn--block" disabled={loading}>
              {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
            </button>
          </div>
        </div>
      </form>

      <Modal open={mapOpen} onClose={() => setMapOpen(false)} title="Choisir sur la carte" wide>
        <MapPicker
          value={coords}
          height={440}
          onChange={(next) => {
            void applyCoords(next);
          }}
        />
        <div className={styles.mapActions}>
          <button type="button" className="btn btn--primary" onClick={() => setMapOpen(false)}>
            Valider ce point
          </button>
        </div>
      </Modal>
    </div>
  );
}
